import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import useRectificationStore from '../../stores/useRectificationStore';
import styles from './ToolCard.module.css';
import { ZoneColors as colors, ZoneNames as zoneNames } from '../../constants/carveReliefConfig';
import NumberInput from '../ui/NumberInput';
import { TOOLS_CONFIG } from '../../constants/tools';
import useEditorStore from '../../stores/useEditorStore';
import { useToolManager } from '../../hooks/useToolManager';

const ScoopRechargeToolCard = () => {
  const scoopRechargeState = useRectificationStore((state) => state.scoopRecharge);
  const setScoopRecharge = useRectificationStore((state) => state.setScoopRecharge);

  const isScoopRechargeApplied = useRectificationStore((state) => state.isScoopRechargeApplied);
  const undoCommand = useRectificationStore((state) => state.currentScoopRechargeCommand);
  const isAppliedFromDraft = useRectificationStore((state) => state.isScoopRechargeAppliedFromDraft);

  const { run } = useToolManager();

  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const handleInfoIconHover = (event) => {
    const rect = event.target.getBoundingClientRect();
    setTooltipPosition({
      x: rect.left + rect.width / 2,
      y: rect.bottom + 8
    });
    setShowTooltip(true);
  };

  useEffect(() => {
    if (scoopRechargeState.zones.length === 0) {
      const initialZones = zoneNames.map((name, index) => ({
        id: index + 1,
        name,
        summitValue: 0.0,
        color: colors[index % colors.length]
      }));

      setScoopRecharge({ zones: initialZones, selectedZoneId: initialZones[0]?.id ?? 0 });
    }
  }, []);

  const addNewZone = () => {
    const currentZones = scoopRechargeState.zones;
    const newId = currentZones.length > 0 ? Math.max(...currentZones.map(z => z.id)) + 1 : 1;
    let newZoneName = `Zone_${newId}`;
    if (currentZones.length < zoneNames.length) {
      newZoneName = zoneNames[(newId - 1) % zoneNames.length] || `Zone_${newId}`;
    }

    // Calculate color index with reverse order when exceeding colors length
    const colorIndex = (newId - 1) % colors.length;
    const cycleNumber = Math.floor((newId - 1) / colors.length);
    const shouldReverse = cycleNumber % 2 === 1; // Reverse on odd cycles
    const finalColorIndex = shouldReverse ? (colors.length - 1 - colorIndex) : colorIndex;

    const newZone = {
      id: newId,
      name: newZoneName,
      summitValue: 0.0,
      color: colors[finalColorIndex]
    };
    
    const newZones = [...currentZones, newZone];
    // Update store; if first zone, also select it
    if (currentZones.length === 0) {
      setScoopRecharge({ zones: newZones, selectedZoneId: newId });
    } else {
      setScoopRecharge({ zones: newZones });
    }
  };

  const deleteZone = (id) => {
    const currentZones = scoopRechargeState.zones;
    const newZones = currentZones.filter(zone => zone.id !== id);
    
    // If the deleted zone was selected, select the first remaining zone (or none)
    const wasSelected = scoopRechargeState.selectedZoneId === id;
    const nextSelected = wasSelected ? (newZones[0]?.id ?? 0) : scoopRechargeState.selectedZoneId;
    setScoopRecharge({ zones: newZones, selectedZoneId: nextSelected });
  };

  const toggleZone = (id) => {
    const currentSelected = scoopRechargeState.selectedZoneId;
    const nextSelected = currentSelected === id ? 0 : id;
    setScoopRecharge({ selectedZoneId: nextSelected });
  };

  const updateSummitValue = (id, value) => {
    const currentZones = scoopRechargeState.zones;
    const updatedZones = currentZones.map(zone => 
      zone.id === id ? { ...zone, summitValue: parseFloat(value) || 0 } : zone
    );
    setScoopRecharge({ zones: updatedZones });
  };

  const resetZonePaint = (id) => {
    const currentZones = scoopRechargeState.zones;
    const updatedZones = currentZones.map(zone =>
      zone.id === id ? {
        ...zone,
        influences: [], // Legacy painting data
        points: [],     // Curve control points
        bezierHandles: [] // Bezier handle data
      } : zone
    );
    setScoopRecharge({ zones: updatedZones });
  };

  const applyScoopRecharge = async () => {
    if(!isScoopRechargeApplied) {
      const { setIsLoading } = useEditorStore.getState();

      try {
        // Show loading overlay while applying
        setIsLoading(true, 'Applying Carve Relief');

        // **FIX: Ensure preview is rebuilt with latest zones before applying**
        // Must await async rebuild to ensure geometry deformations are complete before cloning
        // Pass force=true to ensure calculation happens even if preview is not visible
        const rebuildFn = scoopRechargeState.rebuildPreview;
        if (rebuildFn) {
          await rebuildFn(true); // Force calculation even if preview is disabled
        }

        run({tool: TOOLS_CONFIG.MAIN_PROCESS.RECTIFICATION.main_rectification_scoop_recharge.id, mode: 'apply'});
      } finally {
        // Clear loading state
        setIsLoading(false);
      }
    }
    else if(undoCommand){
      const { resetHistoryUpTo } = useEditorStore.getState();
      resetHistoryUpTo(undoCommand);

      //call undo
      undoCommand.undo();
    }
  }

  const isApplied = isScoopRechargeApplied;
  const isLoadedFromDraft = isApplied && isAppliedFromDraft;

  return (
    <div className='flex flex-col absolute left-[10px] top-[10px]'>
      <div className='bg-white rounded-md p-2 pb-3 shadow-[0_2px_8px_rgba(0,0,0,0.15)] w-[264px] z-[1000] max-h-[70vh] overflow-y-auto border border-gray-200'>
        {(
          <div className={styles.toolCardHeader}>
            <div className="flex items-center gap-2">
              <h3 style={{marginBottom: '0px'}}>Carve Relief</h3>
              {!isApplied && (
                <div className="relative">
                  <button
                    className="w-4 h-4 rounded-full bg-violet-600 text-white text-xs flex items-center justify-center hover:bg-violet-800 transition-colors"
                    onMouseEnter={handleInfoIconHover}
                    onMouseLeave={() => setShowTooltip(false)}
                    onClick={(e) => {
                      handleInfoIconHover(e);
                      setShowTooltip(!showTooltip);
                    }}
                  >
                    i
                  </button>
                </div>
              )}
            </div>
            {!isApplied ? (scoopRechargeState.zones.length === 0 && <p>Configure carve relief zones</p>) : <p>Carve Relief Applied</p>}
          </div>
        )}

        {!isApplied && (
          <div className='mb-4'>
            {/* <div className='mb-4 flex items-center gap-2 ml-2 mr-2'>
              <label className='text-sm text-gray-500'>Brush Size</label>
              <input
                type="range"
                min="0.25"
                max="3"
                step="0.25"
                value={scoopRechargeState.brushRadius}
                onChange={(e) => setScoopRecharge({ brushRadius: parseFloat(e.target.value) || 0.5 })}
                className='w-full h-2 flex-1/3 mt-1 bg-gray-200 rounded-full appearance-none cursor-pointer'
              />
              <label htmlFor="brushRadius" className='flex-1/12 text-sm text-center text-gray-500'>{scoopRechargeState.brushRadius}</label>
            </div> */}
            
            <div className='max-h-92 overflow-y-auto pr-2'>
              {scoopRechargeState.zones.map((zone) => (
                <div key={zone.id} className={styles.zoneContainer}>
                  {zone.id !== scoopRechargeState.selectedZoneId ? (
                    // Collapsed zone
                    <div 
                      className={styles.zoneHeaderCollapsed}
                      onClick={() => toggleZone(zone.id)}
                    >
                      <div 
                        className={styles.zoneIndicator}
                        style={{ backgroundColor: zone.color }}
                      ></div>
                      <span className={styles.zoneName}>{zone.name}</span>
                    </div>
                  ) : (
                    // Expanded zone
                    <div className={styles.zoneExpanded}>
                      {/* Zone Header */}
                      <div className='flex justify-between items-center mb-3'>
                        <div className='flex items-center gap-2'>   {/* Zone Indicator and Name */}
                          <div 
                            className={styles.zoneIndicator}
                            style={{ backgroundColor: zone.color }}
                          ></div>
                          <span className={styles.zoneName}>{zone.name}</span>
                        </div>
                        { zone.id < zoneNames.length + 1 ? (
                        <div className='flex items-center gap-1'> {/* Activate and Delete Buttons */}
                          <button className='bg-blue-400 text-white text-sm px-2 py-[2px] rounded-md cursor-pointer hover:bg-blue-500' title="Reset" onClick={() => resetZonePaint(zone.id)}>
                            Reset
                          </button>
                        </div>) : (
                        <div className='flex items-center gap-1'>
                          <button 
                            className={styles.deleteButton}
                            onClick={() => deleteZone(zone.id)}
                            title="Delete zone"
                          >
                            <img src="/tool_card_icons/scoop_recharge_trash_icon.png" alt="Delete" style={{ width: 16, height: 16 }} />
                          </button>
                        </div>
                        )}
                      </div>

                      {/* Zone Content */}
                      <div className='flex flex-col gap-3'>

                        <div className='flex items-stretch gap-6 h-10'>
                          <label className='text-sm text-gray-500 font-medium self-center'>Carve/Relief</label>

                          <NumberInput
                            value={zone.summitValue}
                            onChange={(e) => updateSummitValue(zone.id, e)}
                            min="-1000"
                            max="1000"
                            step="0.1"
                            unit="mm"
                          />
                          {/* <span className={styles.summitUnit}>mm</span> */}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className={styles.toolCardFooter}>
          {!isApplied ? (
            <>
              <div className='px-2'>
                <label className="flex items-center cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={scoopRechargeState.showControlsForAllZones}
                    onChange={() => setScoopRecharge({ showControlsForAllZones: !scoopRechargeState.showControlsForAllZones })}
                    className="sr-only"
                  />
                  <div className={`w-8 h-5 rounded-full transition-colors duration-200 relative ${scoopRechargeState.showControlsForAllZones ? 'bg-blue-600' : 'bg-gray-300'}`}>
                    <div
                      className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 z-10 ${scoopRechargeState.showControlsForAllZones ? 'translate-x-3' : 'translate-x-0'}`}
                    ></div>
                  </div>
                  <span className={`ml-2 text-xs font-medium text-gray-600`}>
                    Show controls for all zones
                  </span>
                </label>
              </div>
              <button
                className={styles.addButton}
                onClick={addNewZone}
              >
                Add New Zone
              </button>
            </>
          ) : (
            <button className={`px-2 py-1 rounded-md min-w-20 text-white ${
              isLoadedFromDraft
                ? 'bg-gray-400 cursor-not-allowed opacity-60'
                : 'bg-red-400 cursor-pointer hover:bg-red-500 hover:text-white'
            }`}
            onClick={isLoadedFromDraft ? undefined : applyScoopRecharge}
            disabled={isLoadedFromDraft}
            title={isLoadedFromDraft ? "Cannot unapply carve relief loaded from draft" : "Unapply carve relief"}>
              Unapply
            </button>
          )}
        </div>
      </div>
      {!isApplied && (
      <div className='flex items-center justify-between mt-4 px-2'>
        <label className="flex items-center cursor-pointer select-none ml-1">
            <input
              type="checkbox"
              checked={scoopRechargeState.previewVisible}
              onChange={() => setScoopRecharge({ previewVisible: !scoopRechargeState.previewVisible })}
              className="sr-only"
            />
            <div className={`w-10 h-6 rounded-full transition-colors duration-200 relative ${scoopRechargeState.previewVisible ? 'bg-purple-600' : 'bg-gray-300'}`}>
              <div
                className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 z-10 ${scoopRechargeState.previewVisible ? 'translate-x-4' : 'translate-x-0'}`}
              ></div>
            </div>
            <span className={`ml-3 text-sm font-medium ${scoopRechargeState.previewVisible ? 'text-gray-700' : 'text-gray-700'}`}>
              Show Preview
            </span>
          </label>

        <button
          className={`px-2 py-1 rounded-md ml-2 min-w-22 text-white text-lg cursor-pointer bg-violet-600 hover:text-white hover:bg-violet-500`}
          onClick={()=> {
            applyScoopRecharge();
          }}
        >
          Apply
        </button>
      </div>)}

      {/* Tooltip rendered as portal to avoid container clipping */}
      {showTooltip && createPortal(
        <div
          className="fixed z-[9999] w-64 p-3 bg-gray-700 text-white text-xs rounded-lg shadow-lg border border-gray-600"
          style={{
            left: tooltipPosition.x,
            top: tooltipPosition.y,
            transform: 'translateX(-50%)'
          }}
        >
          <div className="space-y-2">
            <div>• <kbd className="bg-gray-600 px-1 rounded text-xs">Alt + Click</kbd> - Add point to selected zone</div>
            <div>• <kbd className="bg-gray-600 px-1 rounded text-xs">Ctrl + Alt + Click</kbd> - Remove point from zone</div>
            <div>• <strong>Drag points</strong> - Move curve points on surface</div>
            <div>• <strong>Drag handles</strong> - Adjust curve shape</div>
          </div>
          {/* Tooltip arrow pointing up to the info icon */}
          <div
            className="absolute w-0 h-0 border-l-4 border-r-4 border-b-4 border-l-transparent border-r-transparent border-b-gray-800"
            style={{
              top: '-8px',
              left: '50%',
              transform: 'translateX(-50%)'
            }}
          ></div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ScoopRechargeToolCard; 